// Seed a DictatedRecipeDraft from an existing recipe detail so BOM-correct
// dictation can refine against real slot labels + quantities.

import { normalizeSlotLabel } from "@/lib/manufacturing/slotLabel";
import type { MfgRecipeDetailResponse, MfgRecipeKind } from "@/lib/manufacturing/types";
import type { DictatedDraftLine, DictatedRecipeDraft } from "@/lib/manufacturing/types/recipeDictation";

/** Material lines only — component/use lines are preserved separately on save. */
export function seedBomCorrectionDraft(
  detail: MfgRecipeDetailResponse,
): DictatedRecipeDraft {
  const recipe = detail.recipe;
  const lines: DictatedDraftLine[] = detail.lines
    .filter((l) => l.line_kind === "material" && l.material_id)
    .map((l) => ({
      material_name_heard: l.material_name || "Unknown material",
      quantity: Number(l.quantity) || 0,
      role: l.role,
      slot_label: normalizeSlotLabel(l.slot_label || ""),
      upgrade_price_delta: l.upgrade_price_delta,
      match: {
        kind: "matched" as const,
        material_id: l.material_id as string,
        canonical_name: l.material_name,
      },
    }))
    .filter((l) => l.quantity !== 0);

  const kind = (recipe.kind ?? "assembly") as MfgRecipeKind;
  return {
    code: recipe.code,
    name: recipe.name,
    kind,
    detectedKind: {
      value: kind,
      confidence: "high",
      reasoning: "Seeded from the selected recipe for BOM correction.",
    },
    tags: Array.isArray(recipe.tags) ? recipe.tags : [],
    lines,
  };
}
